import { useState, useEffect } from "react";
import {
  X,
  User,
  Mail,
  Lock,
  Building,
  Shield,
  Eye,
  EyeOff,
  UserPlus,
  UserCheck,
} from "lucide-react";
import { createUser, updateUser } from "../../../services/userService";
import { getAllCompanies } from "../../../services/companyService";
import { toast } from "react-hot-toast";

const AddEditUserModal = ({
  open,
  onClose,
  onSubmit,
  mode = "add",
  user = null,
}) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    role: "readonly",
    company: "",
    isActive: true,
  });

  const [companies, setCompanies] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoadingCompanies(true);
      try {
        const response = await getAllCompanies();
        if (response.status === "success") {
          setCompanies(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error);
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    if (open) {
      fetchCompanies();

      setForm(
        mode === "edit" && user
          ? {
              firstName: user.firstName || "",
              lastName: user.lastName || "",
              username: user.username || "",
              email: user.email || "",
              password: "",
              role: user.role || "readonly",
              company: user.company?._id || "",
              isActive: user.isActive ?? true,
            }
          : {
              firstName: "",
              lastName: "",
              username: "",
              email: "",
              password: "",
              role: "readonly",
              company: "",
              isActive: true,
            }
      );
      setErrors({});
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let processedValue = type === "checkbox" ? checked : value;

    if ((name === "firstName" || name === "lastName") && type !== "checkbox") {
      processedValue = value.replace(/[^A-Za-z\s.-]/g, "");
    }

    setForm((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    const nameRegex = /^[A-Za-z\s.-]+$/; // Allows letters, spaces, dots, and hyphens

    if (!form.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (!nameRegex.test(form.firstName)) {
      newErrors.firstName =
        "First name can only contain letters, spaces, dots, and hyphens";
    }

    if (!form.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (!nameRegex.test(form.lastName)) {
      newErrors.lastName =
        "Last name can only contain letters, spaces, dots, and hyphens";
    }
    if (!form.username.trim()) newErrors.username = "Username is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Email is invalid";
    if (!isEdit) {
      if (!form.password.trim()) {
        newErrors.password = "Password is required";
      } else if (form.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      } else if (
        !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(form.password)
      ) {
        newErrors.password =
          "Password must contain at least one special character";
      }
    }

    if (!form.company) newErrors.company = "Company is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        const payload = { ...form };
        if (mode === "edit") {
          delete payload.password;
        }

        let response;
        if (mode === "edit") {
          response = await updateUser(user._id, payload);
        } else {
          response = await createUser(payload);
        }

        if (response) {
          console.log("User saved:", response);
          toast.success(
            mode === "edit"
              ? "User Updated Successfully"
              : "User Created Successfully"
          );
          onSubmit();
          onClose();
        } else {
          toast.error(response.data?.message || "Something went wrong");
        }
      } catch (err) {
        console.error(`${mode === "edit" ? "Update" : "Create"} failed:`, err);
        toast.error(
          err.response.data?.message ||
            `Failed to ${
              mode === "edit" ? "update" : "create"
            } user. Please try again.`
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const roleOptions = [
    {
      value: "admin",
      label: "Admin",
      icon: Shield,
      color: "text-red-600 bg-red-50",
    },
    {
      value: "user",
      label: "User",
      icon: User,
      color: "text-blue-600 bg-blue-50",
    },
    {
      value: "readonly",
      label: "Read Only",
      icon: Eye,
      color: "text-gray-600 bg-gray-50",
    },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl transform transition-all duration-300 ease-out animate-in slide-in-from-bottom-4 zoom-in-95 flex flex-col max-h-[90vh]">
        {/* FORM HEADER */}
        <div className="relative bg-white rounded-t-3xl border-b border-gray-200 px-6 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              {isEdit ? (
                <UserCheck className="w-5 h-5 text-gray-600" />
              ) : (
                <UserPlus className="w-5 h-5 text-gray-600" />
              )}
            </div>
            <div className="flex flex-col">
              <h2 className="text-lg font-semibold text-gray-800 tracking-tight">
                {isEdit ? "Edit User" : "Add New User"}
              </h2>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-100 transition"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* FORM CONTENT */}
        <div className="flex-1 px-6 py-5 overflow-y-auto space-y-6 text-sm text-gray-700 bg-white">
          {/* Row: First & Last Name */}
          <div className="grid grid-cols-2 gap-4">
            {[
              {
                name: "firstName",
                label: "First Name",
                icon: User,
                placeholder: "John",
              },
              {
                name: "lastName",
                label: "Last Name",
                icon: User,
                placeholder: "Doe",
              },
            ].map(({ name, label, icon: Icon, placeholder }) => (
              <div key={name}>
                <label className="mb-1 block font-medium">{label}</label>
                <div className="relative">
                  <input
                    type="text"
                    name={name}
                    value={form[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white transition-all placeholder:text-gray-400 focus:outline-none focus:ring-1 
                                            ${
                                              errors[name]
                                                ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                                : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                            }`}
                  />
                  <Icon className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                {errors[name] && (
                  <p className="text-red-500 text-xs mt-1">{errors[name]}</p>
                )}
              </div>
            ))}
          </div>

          {/* Username */}
          <div>
            <label className="mb-1 block font-medium">Username</label>
            <div className="relative">
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="johndoe"
                className={`w-full rounded-lg border pl-8 pr-3 py-2.5 bg-white transition-all placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.username
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
              />
              <span className="absolute left-3 top-2.5 text-gray-400 text-sm">
                @
              </span>
            </div>
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="mb-1 block font-medium">Email</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="john.doe@example.com"
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 bg-white transition-all placeholder:text-gray-400 focus:outline-none focus:ring-1
                                    ${
                                      errors.email
                                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                                    }`}
              />
              <Mail className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
            </div>
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Only show password field in add mode */}
          {!isEdit && (
            <div>
              <label className="mb-1 block font-medium">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={`w-full rounded-lg border px-4 py-2.5 pr-20 bg-white transition-all placeholder:text-gray-400 focus:outline-none focus:ring-1
              ${
                errors.password
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
              }`}
                />
                <Lock className="absolute right-10 top-3 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
          )}

          {/* Role Selection */}
          <div>
            <label className="mb-2 block font-medium">Role</label>
            <div className="flex flex-wrap gap-2">
              {roleOptions.map(({ value, label, icon: Icon }) => {
                const selected = form.role === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({ ...prev, role: value }))
                    }
                    className={`
                                            flex items-center gap-2 px-3 py-2 rounded-full border transition
                                            text-sm font-medium
                                            ${
                                              selected
                                                ? "bg-[#27ade3]/10 border-[#27ade3] text-[#1a325d]"
                                                : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                                            }
                                        `}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Company Select */}
          <div>
            <label className="mb-1 block font-medium">Company</label>
            <div className="relative">
              <select
                name="company"
                value={form.company}
                onChange={handleChange}
                disabled={isLoadingCompanies}
                className={`w-full rounded-lg border px-4 py-2.5 pr-10 appearance-none bg-white focus:outline-none focus:ring-1
                    ${
                      errors.company
                        ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                        : "border-gray-300 focus:border-[#27ade3] focus:ring-[#cce5f8]"
                    } ${
                  isLoadingCompanies ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <option value="">
                  {isLoadingCompanies
                    ? "Loading companies..."
                    : "Select a company"}
                </option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.name}
                  </option>
                ))}
              </select>
              <Building className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
              {isLoadingCompanies && (
                <div className="absolute right-10 top-3">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
            {errors.company && (
              <p className="text-red-500 text-xs mt-1">{errors.company}</p>
            )}
          </div>

          {/* Active Status */}
          <div className="flex items-center justify-between border rounded-lg px-4 py-3 bg-white">
            <div className="flex items-center gap-2">
              {/* <div className="h-3 w-3 rounded-full bg-green-400 shadow" /> */}
              <p className="text-sm font-medium text-gray-800">Active Status</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={() =>
                  setForm((prev) => ({ ...prev, isActive: !prev.isActive }))
                }
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-[#27ade3] transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-md transition-transform peer-checked:translate-x-5" />
            </label>
          </div>
        </div>

        {/* FORM FOOTER */}
        <div className="bg-white rounded-b-3xl border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-5 py-2.5 text-sm rounded-lg text-white flex items-center gap-2 transition-colors font-medium 
                        ${
                          isSubmitting
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-[#1a325d] hover:bg-[#21426e]"
                        }`}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{isEdit ? "Saving..." : "Creating..."}</span>
              </>
            ) : (
              <span>{isEdit ? "Update User" : "Add User"}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditUserModal;
